// Live's slot tooltips (#260, live's `media-validation.ts` formatters): a mode's media limits
// (#258, `media_limits`) as the lines live shows, in its words and order. A mode without limits
// shows none, as live's.
import type { AudioLimits, ImageLimits, VideoLimits } from "../../../api/generated/model";

const has = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined;

/** Live's `formatImageConstraints`. */
export function imageLimitLines(limits: ImageLimits | null | undefined): string[] {
  if (limits === null || limits === undefined) return [];
  const lines: string[] = [];
  if (has(limits.max_size_mb)) lines.push(`Max size: ${limits.max_size_mb} MB`);
  if (has(limits.min_width_px) && has(limits.min_height_px)) {
    lines.push(`Min resolution: ${limits.min_width_px}×${limits.min_height_px} px`);
  }
  if (has(limits.min_aspect_ratio) && has(limits.max_aspect_ratio)) {
    lines.push(`Aspect ratio: ${limits.min_aspect_ratio} – ${limits.max_aspect_ratio}`);
  }
  if ((limits.accepted_formats?.length ?? 0) > 0) {
    lines.push(`Formats: ${limits.accepted_formats!.join(", ")}`);
  }
  return lines;
}

/** Live's `formatVideoConstraints`. */
export function videoLimitLines(limits: VideoLimits | null | undefined): string[] {
  if (limits === null || limits === undefined) return [];
  const lines: string[] = [];
  if (has(limits.max_size_mb)) lines.push(`Max size: ${limits.max_size_mb} MB`);
  const { min_resolution_px: low, max_resolution_px: high } = limits;
  if (has(low) && has(high)) lines.push(`Resolution: ${low} – ${high} px (min side)`);
  else if (has(low)) lines.push(`Min resolution: ${low} px`);
  else if (has(high)) lines.push(`Max resolution: ${high} px`);
  lines.push(...durationLines(limits.min_duration_sec, limits.max_duration_sec));
  if ((limits.accepted_formats?.length ?? 0) > 0) {
    lines.push(`Formats: ${limits.accepted_formats!.join(", ")}`);
  }
  return lines;
}

/** Live's `formatAudioConstraints`. */
export function audioLimitLines(limits: AudioLimits | null | undefined): string[] {
  if (limits === null || limits === undefined) return [];
  const lines: string[] = [];
  if (has(limits.max_size_mb)) lines.push(`Max size: ${limits.max_size_mb} MB`);
  lines.push(...durationLines(limits.min_duration_sec, limits.max_duration_sec));
  if (has(limits.combined_max_duration_sec)) {
    lines.push(`Combined max duration: ${limits.combined_max_duration_sec} s`);
  }
  if ((limits.accepted_formats?.length ?? 0) > 0) {
    lines.push(`Formats: ${limits.accepted_formats!.join(", ")}`);
  }
  return lines;
}

function durationLines(low: number | null | undefined, high: number | null | undefined) {
  if (has(low) && has(high)) return [`Duration: ${low} – ${high} s`];
  if (has(low)) return [`Min duration: ${low} s`];
  if (has(high)) return [`Max duration: ${high} s`];
  return [];
}
